// 新增委派精靈（委派方，四步；SPEC #org-tree-ui 第 3 點、o11 / o12 / o13 / o24）：
//   1 代碼 → 確認對方（組織名稱、審核徽章、組織代號、請求人與 email 網域；未審核黃字）
//   2 節點：結構樹多選；最上面「整個組織（現在與之後的節點、戶）」= 根節點（高風險：要填理由），勾了下面全部帶勾、不能逐一取消
//   3 範圍與期限：預設只勾「看」；匯出 / 撤防標「高風險：要填理由」；固定三行（留在委派方、API / webhook 不取得、MFA 套用 IP 不套用）
//   4 確認：涵蓋（結構總數）、範圍、期限、歷史；通知誰；高風險（owner / org_admin 都一樣）或建立者同時是受託組織成員 → 理由必填，送出立即生效
//     （Eddy 2026-10-21 選單：不設第二位核可；platform#90）
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isKinioError } from '@kinio/web-sdk';
import { Dialog } from '../../components/Dialog';
import type { MyOrg } from '../../lib/planeApi';
import { DELEGATION_SCOPES, HIGH_RISK_SCOPES, treeApi, type DelegationCodePreview, type OrgTreeItem } from '../../lib/orgTreeApi';
import { actionWords } from '../../lib/orgTreeUi';
import { StructureTree, rootIdOf, useRootLevel } from './StructureTree';
import { otErr } from './otErr';
import { dateStamp } from '../../lib/timefmt';

const VIEW = ['device.read', 'media.live'];

export function DelegationWizard({ org, initialCode, close, done }: { org: MyOrg; initialCode?: string; close(): void; done(msg: string): void }) {
  const { t } = useTranslation();
  const root = useRootLevel(org);
  const rootId = rootIdOf(root.data);
  const [step, setStep] = useState(0);
  const [code, setCode] = useState(initialCode ?? '');
  const [peer, setPeer] = useState<DelegationCodePreview | null>(null);
  const [whole, setWhole] = useState(false);
  const [nodes, setNodes] = useState<Map<string, OrgTreeItem>>(new Map());
  const [scopes, setScopes] = useState<string[]>(VIEW);
  const [until, setUntil] = useState('');
  const [histDays, setHistDays] = useState(0);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 允許受託方回放本機錄影全部歷史（SPEC #device-permissions-local-playback）：打開算高風險（要理由）
  const [lpf, setLpf] = useState(false);
  const highRisk = whole || lpf || scopes.some((s) => HIGH_RISK_SCOPES.includes(s));
  const needReason = highRisk || !!peer?.self_related;
  const totals = whole
    ? root.data?.root?.subtree
    : [...nodes.values()].reduce((a, n) => ({ nodes: a.nodes + (n.subtree?.nodes ?? 0), homes: a.homes + (n.subtree?.homes ?? 0), devices: a.devices + (n.subtree?.devices ?? 0) }), { nodes: 0, homes: 0, devices: 0 });

  async function checkCode() {
    setBusy(true);
    setErr(null);
    try {
      setPeer(await treeApi.previewCode(org, code.trim()));
      setStep(1);
    } catch (e) {
      setErr(otErr(t, e));
    } finally {
      setBusy(false);
    }
  }
  async function create() {
    if (needReason && !reason.trim()) return setErr(highRisk ? t('ot.err_high_risk_reason') : t('org.err_REASON_REQUIRED'));
    setBusy(true);
    setErr(null);
    try {
      await treeApi.createDelegation(org, {
        code: code.trim(),
        node_ids: whole ? [rootId] : [...nodes.keys()],
        scopes,
        expires_at: until ? new Date(until + 'T23:59:59').toISOString() : null,
        history_from: histDays > 0 ? new Date(Date.now() - histDays * 864e5).toISOString() : null,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        ...(lpf && scopes.includes('recording.read') ? { local_playback_full: true } : {}),
      });
      done(t('ot.dlg_created', { org: peer?.to_org_name ?? '' }));
      close();
    } catch (e) {
      if (isKinioError(e) && e.code === 'REASON_REQUIRED') setPeer((p) => (p ? { ...p, self_related: true } : p));
      setErr(otErr(t, e));
    } finally {
      setBusy(false);
    }
  }
  const steps = [t('ot.wz_code'), t('ot.wz_nodes'), t('ot.wz_scope'), t('ot.wz_confirm')];
  const nodeNames = whole ? t('ot.whole_org') : [...nodes.values()].map((n) => n.name).join('、');

  return (
    <Dialog open title={t('ot.dlg_new_title')} onClose={close} wide>
      <div className="form" data-testid="dlg-wizard" data-step={step}>
        <ol className="ot-steps">
          {steps.map((s, i) => (
            <li key={s} aria-current={i === step ? 'step' : undefined} className={i === step ? 'on' : i < step ? 'done' : ''}>
              {s}
            </li>
          ))}
        </ol>
        {step === 0 && (
          <>
            <label className="field">
              <span>{t('ot.dlg_code_input')}</span>
              <input className="input mono" value={code} onChange={(e) => setCode(e.target.value)} placeholder="DLG-XXXX-XXXX" autoFocus aria-label={t('ot.dlg_code_input')} />
            </label>
            <p className="muted small">{t('ot.dlg_code_hint')}</p>
          </>
        )}
        {step >= 1 && peer && (
          <div className="ot-peer card" data-testid="dlg-peer">
            <strong>{peer.to_org_name}</strong>
            {(peer.verified_kinds?.length ?? 0) > 0 && <span className="badge ot-verified">{t('ot.verified', { kinds: peer.verified_kinds!.join('、') })}</span>}
            {peer.to_org_code && <span className="mono small"> {t('ot.org_code', { code: peer.to_org_code })}</span>}
            {(peer.requested_by_name || peer.requested_by_email_domain) && (
              <div className="muted small">{t('ot.requested_by', { name: peer.requested_by_name ?? '', domain: peer.requested_by_email_domain ?? '' })}</div>
            )}
            {!(peer.verified_kinds?.length ?? 0) && step === 1 && <p className="ot-warn small">{t('ot.unverified_warn')}</p>}
          </div>
        )}
        {step === 1 && (
          <>
            <label className="row-inline ot-whole" data-testid="whole-org">
              <input type="checkbox" checked={whole} onChange={(e) => setWhole(e.target.checked)} />
              {t('ot.whole_org_check')} <span className="badge badge-warn">{t('ot.high_risk_badge')}</span>
            </label>
            <StructureTree
              org={org}
              showLock={false}
              showUnassigned={false}
              nodeChecks={{
                set: new Set(nodes.keys()),
                disabled: whole,
                toggle: (it) => {
                  const m = new Map(nodes);
                  if (m.has(it.node_id!)) m.delete(it.node_id!);
                  else m.set(it.node_id!, it);
                  setNodes(m);
                },
              }}
            />
            <p className="muted small">{t('ot.single_home_hint')}</p>
          </>
        )}
        {step === 2 && (
          <>
            <fieldset className="field">
              <legend>{t('ot.dlg_scope')}</legend>
              <div className="ot-scope-grid">
                {['view', ...DELEGATION_SCOPES.filter((s) => !VIEW.includes(s))].map((s) => {
                  const acts = s === 'view' ? VIEW : [s];
                  const on = acts.every((a) => scopes.includes(a));
                  return (
                    <label key={s} className="row-inline">
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={s === 'view'}
                        onChange={(e) => setScopes(e.target.checked ? [...new Set([...scopes, ...acts])] : scopes.filter((x) => !acts.includes(x)))}
                      />
                      {t('ot.scope_' + s.replace(/\./g, '_'))}
                      {HIGH_RISK_SCOPES.includes(s) && <span className="badge badge-warn">{t('ot.high_risk_badge')}</span>}
                    </label>
                  );
                })}
              </div>
            </fieldset>
            <ul className="ot-fixed small">
              <li>{t('ot.dlg_fixed_1')}</li>
              <li>{t('ot.dlg_fixed_2')}</li>
              <li>{t('ot.dlg_fixed_3')}</li>
            </ul>
            <label className="field">
              <span>{t('ot.term')}</span>
              <select className="input" value={until ? 'date' : 'forever'} onChange={(e) => setUntil(e.target.value === 'date' ? new Date(Date.now() + 365 * 864e5).toISOString().slice(0, 10) : '')}>
                <option value="forever">{t('ot.dlg_forever')}</option>
                <option value="date">{t('ot.dlg_until')}</option>
              </select>
            </label>
            {until && <input className="input" type="date" value={until} onChange={(e) => setUntil(e.target.value)} aria-label={t('ot.dlg_until')} />}
            <label className="field">
              <span>{t('ot.dlg_history')}</span>
              <select className="input" value={histDays ? 'back' : 'now'} onChange={(e) => setHistDays(e.target.value === 'back' ? 7 : 0)}>
                <option value="now">{t('ot.dlg_history_now')}</option>
                <option value="back">{t('ot.dlg_history_back')}</option>
              </select>
            </label>
            {histDays > 0 && (
              <label className="row-inline">
                <input className="input" type="number" min={1} max={30} style={{ width: 80 }} value={histDays} onChange={(e) => setHistDays(Math.min(30, Math.max(1, Number(e.target.value) || 1)))} />
                {t('ot.days_back')}
              </label>
            )}
          </>
        )}
        {step === 3 && (
          <div data-testid="dlg-summary">
            <table className="ot-kv">
              <tbody>
                <tr>
                  <th>{t('ot.wz_nodes')}</th>
                  <td>
                    {nodeNames}
                    {totals && <span className="muted small"> · {t('ot.n_homes_devices', { homes: totals.homes, devices: totals.devices })}</span>}
                  </td>
                </tr>
                <tr>
                  <th>{t('ot.dlg_scope')}</th>
                  <td>{actionWords(t, scopes)}</td>
                </tr>
                <tr>
                  <th>{t('ot.term')}</th>
                  <td>{until ? t('ot.until', { date: dateStamp(until) }) : t('ot.dlg_forever')}</td>
                </tr>
                <tr>
                  <th>{t('ot.dlg_history')}</th>
                  <td>{histDays ? t('ot.history_days', { count: histDays }) : t('ot.dlg_history_now')}</td>
                </tr>
              </tbody>
            </table>
            {scopes.includes('recording.read') && (
              <>
                <label className="row-inline">
                  <input type="checkbox" checked={lpf} onChange={(e) => setLpf(e.target.checked)} data-testid="dlg-lpf" />
                  {t('ot.local_playback_full')}
                </label>
                <p className="muted small">{t('ot.local_playback_full_hint')}</p>
              </>
            )}
            <p className="ot-warn small">
              {highRisk ? t('ot.dlg_notify_high_risk', { from: org.display_name, to: peer?.to_org_name ?? '' }) : t('ot.dlg_notify', { from: org.display_name, to: peer?.to_org_name ?? '' })}
            </p>
            {needReason && (
              <>
                {peer?.self_related && <p className="ot-warn small">{t('ot.dlg_self_related', { to: peer.to_org_name })}</p>}
                <label className="field">
                  <span>{t('ot.reason')} *</span>
                  <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} aria-label={t('ot.reason')} />
                </label>
              </>
            )}
          </div>
        )}
        {err && (
          <p className="error" role="alert">
            {err}
          </p>
        )}
        <div className="dialog-actions">
          <button className="btn" onClick={step === 0 ? close : () => setStep(step - 1)}>
            {step === 0 ? t('common.cancel') : t('common.back')}
          </button>
          {step === 0 && (
            <button className="btn btn-primary" disabled={busy || !code.trim()} onClick={() => void checkCode()}>
              {t('ot.next')}
            </button>
          )}
          {(step === 1 || step === 2) && (
            <button className="btn btn-primary" disabled={step === 1 && !whole && nodes.size === 0} onClick={() => setStep(step + 1)}>
              {t('ot.next')}
            </button>
          )}
          {step === 3 && (
            <button className="btn btn-primary" disabled={busy || (needReason && !reason.trim())} onClick={() => void create()} data-testid="dlg-create">
              {t('ot.dlg_create')}
              {t('ot.reauth_suffix')}
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
